import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Inventory } from "types/inventory";
import { CaptureAngle, StepConfig } from "components/Photobooth/photoBoothSteps";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { useHotkeys } from "react-hotkeys-hook";
import { audioSources } from "constants/audio_sources";
import { WebcamComponent } from "components/WebcamComponent";
import { ArrowBack } from "@mui/icons-material";
import { useSavedCameraConfiguration } from "pages/PhotoBooth/ConfigurationPage";

export function CapturePhotosPage() {
  const { photoSet } = useParams();
  const photoSetNumber = Number(photoSet);
  const { inventory, takePhotos, deviceTypeStep } = useOutletContext<{
    inventory?: Inventory;
    takePhotos: (angles: CaptureAngle[], filenames: string[]) => Promise<void>;
    deviceTypeStep: StepConfig[];
  }>();

  const [capture, setCapture] = React.useState(false);
  const navigate = useNavigate();
  const videoElement = useRef<HTMLVideoElement>(null);

  const { savedCameraConfiguration } = useSavedCameraConfiguration();
  const stepConfig = deviceTypeStep[photoSetNumber - 1];
  const isLastStep = photoSetNumber === deviceTypeStep.length;
  const onCaptureAudioElement = useMemo(() => new Audio(audioSources.onCaptureReady), []);

  const playAudioElement = useCallback(async () => {
    try {
      await onCaptureAudioElement.play();
    } catch (e) {
      console.error("Failed to play audio", e);
    }
  }, [onCaptureAudioElement]);

  const handleCapture = useCallback(async () => {
    const { angles, filenames } = stepConfig;
    playAudioElement();
    await takePhotos(angles, filenames);
    navigate(!isLastStep ? `../capture/${photoSetNumber + 1}` : "../review");
  }, [isLastStep, navigate, photoSetNumber, playAudioElement, stepConfig, takePhotos]);

  const captureFnRef = useRef(handleCapture);

  useLayoutEffect(() => {
    captureFnRef.current = handleCapture;
  }, [handleCapture]);

  useEffect(() => {
    let cancel = false;
    if (!capture) return;

    captureFnRef.current().then(() => {
      if (cancel) return;
      setCapture(false);
    });
    return function cleanup() {
      cancel = true;
    };
  }, [capture]);

  useHotkeys("mod+g", () => setCapture(true), {
    description: "Capture Photo / Confirm",
    enableOnFormTags: ["INPUT"],
    preventDefault: true,
  });

  useEffect(() => {
    if (videoElement.current) {
      videoElement.current.load();
      videoElement.current.play();
    }
  }, [stepConfig.gifSource]);

  if (!inventory || !stepConfig) return null;

  return (
    <Box component={Paper} variant="outlined" padding={4}>
      <Stack spacing={4} justifyContent="space-evenly">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="24px" fontWeight={600}>
            Capture Photos
          </Typography>
          <Typography fontSize="14px" fontWeight={400}>
            (Batch {photoSet})
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-evenly">
          <Stack>
            <Typography fontSize="18px" fontWeight={600}>
              Position {inventory.deviceType}
            </Typography>
            <Typography fontSize="14px" fontWeight={400} color="text.secondary">
              Orient the device as shown.
            </Typography>
            <Box sx={{ maxHeight: 300, aspectRatio: 1.0 }}>
              <video autoPlay muted style={{ height: "100%", objectFit: "cover" }} ref={videoElement}>
                <source src={stepConfig.gifSource} type="video/webm" />
                Your browser does not support the video tag.
              </video>
            </Box>
          </Stack>
          {savedCameraConfiguration && (
            <WebcamComponent gridLines device={savedCameraConfiguration[CaptureAngle.Top]} />
          )}
        </Stack>
        <Divider />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            component={Link}
            to={"/resale-photo-booth"}
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography fontSize="14px" fontWeight={400}>
              Press foot pedal
            </Typography>
            <Typography fontSize="14px" fontWeight={400}>
              or
            </Typography>
            <Button onClick={() => setCapture(true)} variant="contained">
              Capture Photos
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
